'use client';

import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { useDesktopTradeCallbacks } from '@flipster/features/src/chart-order-drag/use-desktop-trade-callbacks';
import { useAbFlag } from '@flipster/features/src/shared/ab-test';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useRouter } from '@flipster/i18n/routing';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { PathnameScope } from '@flipster/react/providers/route';
import { css } from '@flipster/styled-system/css';
import { TvChartWeb, TvScript } from '@flipster/tvchart';

import { commonFormBunja } from '@web/app/[locale]/(web)/trade/_misc/order-form/common';
import { formUserInputBunja } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/form';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import InfoPanel from './InfoPanel';

type TabType = 'chart' | 'info';
const Tabs = createTabsComponent<TabType>();

const chartAndInfoBunja = bunja(() => {
  const pathname = bunja.use(PathnameScope);
  const isSpotPath = pathname.includes('/spot/');

  return { isSpotPath };
});

const ChartAndInfo = () => {
  const t = useTranslations();
  const router = useRouter();
  const { symbol } = useBunja(tradeSymbolBunja);
  const { priceTypeAtom } = useBunja(commonFormBunja);
  const { triggerPriceAtom } = useBunja(formUserInputBunja);
  const setPriceType = useSetAtom(priceTypeAtom);
  const setTriggerPrice = useSetAtom(triggerPriceAtom);
  const [tab, setTab] = useState<TabType>('chart'); // 'chart' | 'info'
  const { isSpotPath } = useBunja(chartAndInfoBunja);
  const { isLoggedIn } = useAuth();

  const isSpotLimitOrderEnabled = useAbFlag('25q4-spot-limit-order', 'on');
  // 데스크톱 거래 콜백 묶음 (v2/trade API 직접 실행). webview의 useWebviewTradeCallbacks(브리지 위임)와 대칭.
  const { tradeCallbacks, dragOpener } = useDesktopTradeCallbacks({ symbol });

  const onWhaleTrackerCopyClick = (entryPrice: string) => {
    if (isLoggedIn) {
      setPriceType('trigger');
      setTriggerPrice(entryPrice);
      snackbar.success({ message: 'Copied to order form' });
    } else {
      router.push('/login');
    }
  };

  // Reset tab on trade symbol changed
  useEffect(() => {
    setTab('chart');
  }, [symbol]);

  return (
    <section
      className={css({
        gridArea: 'chart',
        flex: 1,
        // minH:0 allows flex:1 to shrink below content size in the panel wrapper
        minH: { tablet: 0 },
        h: { tablet: '100%' },
        rounded: 'var(--grid-rounded)',
        bgColor: 'background.default.level-0',
        overflow: 'hidden',
        position: 'relative',
      })}
    >
      <Tabs
        value={tab}
        type='scrollable'
        border
        dragHandle
        onChange={setTab}
        css={{ tablet: { display: 'flex', flexDir: 'column', h: '100%' } }}
        panelCss={{
          tablet: {
            flex: 1,
            minH: 0,
            display: 'flex',
            flexDir: 'column',
          },
        }}
      >
        <Tabs.Tab label={t('Autogen_common.r251126.s_Chart')} value='chart'>
          <div
            className={css({
              pos: 'relative',
              h: { base: 320, tablet: '100%' },
              flex: { tablet: 1 },
              minH: { tablet: 0 },
            })}
          >
            <TvScript />
            <TvChartWeb
              symbol={symbol}
              isSpot={isSpotPath}
              isLoggedIn={isLoggedIn}
              onWhaleTrackerCopyClick={onWhaleTrackerCopyClick}
              {...tradeCallbacks}
              featureFlags={{
                '25q4-spot-limit-order': isSpotLimitOrderEnabled,
              }}
            />
            {dragOpener}
          </div>
        </Tabs.Tab>
        <Tabs.Tab label={t('Autogen_common.r251126.s_Info')} value='info'>
          {symbol !== 'dummy' && <InfoPanel key={symbol} />}
        </Tabs.Tab>
      </Tabs>
    </section>
  );
};

export default ChartAndInfo;
